import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
} from "react-native";
import { FontAwesome6 } from "@expo/vector-icons";
import { WeekConfiguration } from "../common/utils/peak-off-peak-field";
import type { UnavailableTimesJSON } from "../common/utils/unavailable-times-utils";
import { jsonToUnavailable } from "../common/utils/unavailable-times-utils";

export type UnavailableHours = {
  [day: string]: boolean[];
};

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DAY_ABBREVS: Record<string, string> = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const formatHour = (h: number): string => {
  if (h === 0) return "12a";
  if (h < 12) return `${h}a`;
  if (h === 12) return "12p";
  return `${h - 12}p`;
};

const createEmptyUnavailable = (): UnavailableHours => {
  const result: UnavailableHours = {};
  DAYS.forEach((day) => {
    result[day] = new Array(24).fill(false);
  });
  return result;
};

const peakToUnavailable = (peak: WeekConfiguration): UnavailableHours => {
  const result = createEmptyUnavailable();
  for (const [day, range] of Object.entries(peak)) {
    if (!range || range.start === range.end) continue;
    for (let h = range.start; h < Math.min(range.end, 24); h++) {
      if (h >= 0) result[day][h] = true;
    }
  }
  return result;
};

const isPeakHour = (
  day: string,
  hour: number,
  peak: WeekConfiguration
): boolean => {
  const range = peak[day];
  if (!range || range.start === range.end) return false;
  return hour >= range.start && hour < range.end;
};

interface UnavailableTimesGridProps {
  peakSelection: WeekConfiguration;
  initialValue?: UnavailableTimesJSON;
  onChange?: (unavailable: UnavailableHours) => void;
}

const CELL_SIZE = 30;
const CELL_GAP = 1.5;
const DAY_LABEL_WIDTH = 44;
const HEADER_HEIGHT = 24;

const UnavailableTimesGrid: React.FC<UnavailableTimesGridProps> = ({
  peakSelection,
  initialValue,
  onChange,
}) => {
  const [unavailable, setUnavailable] = useState<UnavailableHours>(() =>
    initialValue ? jsonToUnavailable(initialValue) : createEmptyUnavailable()
  );
  const [isCollapsed, setIsCollapsed] = useState(false);
  const didSeed = useRef(false);
  const userAction = useRef(false);

  useEffect(() => {
    if (initialValue && !didSeed.current) {
      const seeded = jsonToUnavailable(initialValue);
      const hasAny = DAYS.some((d) => seeded[d].some(Boolean));
      if (hasAny) {
        setUnavailable(seeded);
        didSeed.current = true;
      }
    }
  }, [initialValue]);

  useEffect(() => {
    if (userAction.current) {
      userAction.current = false;
      onChange?.(unavailable);
    }
  }, [unavailable, onChange]);

  const toggleCell = useCallback(
    (day: string, hour: number) => {
      userAction.current = true;
      setUnavailable((prev) => {
        const next = { ...prev, [day]: [...prev[day]] };
        next[day][hour] = !next[day][hour];
        return next;
      });
    },
    []
  );

  const toggleDay = useCallback(
    (day: string) => {
      userAction.current = true;
      setUnavailable((prev) => {
        const allOn = prev[day].every((v) => v);
        return { ...prev, [day]: new Array(24).fill(!allOn) };
      });
    },
    []
  );

  const toggleHour = useCallback(
    (hour: number) => {
      userAction.current = true;
      setUnavailable((prev) => {
        const next: UnavailableHours = {};
        const allOn = DAYS.every((day) => prev[day][hour]);
        DAYS.forEach((day) => {
          next[day] = [...prev[day]];
          next[day][hour] = !allOn;
        });
        return next;
      });
    },
    []
  );

  const copyFromPeak = useCallback(() => {
    userAction.current = true;
    setUnavailable(peakToUnavailable(peakSelection));
  }, [peakSelection]);

  const clearAll = useCallback(() => {
    userAction.current = true;
    setUnavailable(createEmptyUnavailable());
  }, []);

  const totalUnavailable = DAYS.reduce(
    (sum, day) => sum + unavailable[day].filter(Boolean).length,
    0
  );

  return (
    <View style={styles.container}>
      {/* Title bar — always visible, acts as collapse toggle */}
      <Pressable
        style={styles.titleBar}
        onPress={() => setIsCollapsed((v) => !v)}
      >
        <View style={styles.titleLeft}>
          <FontAwesome6
            name="clock"
            size={14}
            color="#F97777"
            style={{ marginRight: 8 }}
          />
          <Text style={styles.title}>Unavailable Times</Text>
          {totalUnavailable > 0 && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{totalUnavailable}h</Text>
            </View>
          )}
        </View>
        <FontAwesome6
          name={isCollapsed ? "chevron-right" : "chevron-down"}
          size={12}
          color="#9CA3AF"
        />
      </Pressable>

      {!isCollapsed && (
        <>
          {/* Action chips */}
          <View style={styles.chipRow}>
            <Pressable style={styles.chipPeak} onPress={copyFromPeak}>
              <FontAwesome6 name="bolt" size={10} color="#51B868" />
              <Text style={styles.chipPeakText}>Copy Peak Hours</Text>
            </Pressable>
            {totalUnavailable > 0 && (
              <Pressable style={styles.chipClear} onPress={clearAll}>
                <FontAwesome6 name="xmark" size={10} color="#9CA3AF" />
                <Text style={styles.chipClearText}>Clear</Text>
              </Pressable>
            )}
          </View>

          <Text style={styles.helpText}>
            Tap cells to block hours. Tap a day or hour label to toggle
            entire rows/columns.
          </Text>

          {/* Grid */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator
            style={styles.scrollWrap}
            contentContainerStyle={styles.scrollContent}
          >
            <View>
              {/* Hour headers */}
              <View style={styles.gridRow}>
                <View style={{ width: DAY_LABEL_WIDTH }} />
                {HOURS.map((h) => (
                  <Pressable
                    key={`hdr-${h}`}
                    onPress={() => toggleHour(h)}
                    style={[
                      styles.hourHeaderCell,
                      { width: CELL_SIZE + CELL_GAP * 2, height: HEADER_HEIGHT },
                    ]}
                  >
                    <Text
                      style={
                        h % 3 === 0 ? styles.hourLabelMajor : styles.hourLabelMinor
                      }
                    >
                      {h % 3 === 0 ? formatHour(h) : "·"}
                    </Text>
                  </Pressable>
                ))}
              </View>

              {/* Day rows */}
              {DAYS.map((day) => {
                const allOn = unavailable[day].every(Boolean);
                return (
                  <View key={day} style={styles.gridRow}>
                    <Pressable
                      style={[
                        styles.dayLabel,
                        { width: DAY_LABEL_WIDTH, height: CELL_SIZE + CELL_GAP * 2 },
                        allOn && styles.dayLabelActive,
                      ]}
                      onPress={() => toggleDay(day)}
                    >
                      <Text
                        style={[
                          styles.dayLabelText,
                          allOn && styles.dayLabelTextActive,
                        ]}
                      >
                        {DAY_ABBREVS[day]}
                      </Text>
                    </Pressable>

                    {HOURS.map((h) => {
                      const blocked = unavailable[day][h];
                      const peak = isPeakHour(day, h, peakSelection);
                      return (
                        <Pressable
                          key={`${day}-${h}`}
                          onPress={() => toggleCell(day, h)}
                          style={[
                            styles.cell,
                            {
                              width: CELL_SIZE,
                              height: CELL_SIZE,
                              margin: CELL_GAP,
                            },
                            blocked
                              ? styles.cellBlocked
                              : peak
                                ? styles.cellPeakHint
                                : styles.cellOpen,
                          ]}
                        >
                          {peak && !blocked && <View style={styles.peakDot} />}
                        </Pressable>
                      );
                    })}
                  </View>
                );
              })}
            </View>
          </ScrollView>

          {/* Legend */}
          <View style={styles.legend}>
            <View style={styles.legendItem}>
              <View style={[styles.legendSwatch, styles.cellOpen]} />
              <Text style={styles.legendLabel}>Available</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendSwatch, styles.cellBlocked]} />
              <Text style={styles.legendLabel}>Unavailable</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendSwatch, styles.cellPeakHint]}>
                <View style={styles.peakDot} />
              </View>
              <Text style={styles.legendLabel}>Peak hour</Text>
            </View>
          </View>
        </>
      )}
    </View>
  );
};

export default UnavailableTimesGrid;

const styles = StyleSheet.create({
  container: {
    marginTop: 20,
    backgroundColor: "#152C2B",
    borderRadius: 12,
    overflow: "hidden",
  },

  titleBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  titleLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  title: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "700",
  },
  badge: {
    marginLeft: 8,
    backgroundColor: "#3D1F1F",
    borderRadius: 10,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  badgeText: {
    color: "#F97777",
    fontSize: 11,
    fontWeight: "700",
  },

  chipRow: {
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  chipPeak: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    backgroundColor: "#1A3F3D",
    borderColor: "#51B868",
    borderWidth: 1,
    borderRadius: 16,
    paddingHorizontal: 11,
    paddingVertical: 5,
  },
  chipPeakText: {
    color: "#51B868",
    fontSize: 12,
    fontWeight: "600",
  },
  chipClear: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    borderColor: "#4B5563",
    borderWidth: 1,
    borderRadius: 16,
    paddingHorizontal: 11,
    paddingVertical: 5,
  },
  chipClearText: {
    color: "#9CA3AF",
    fontSize: 12,
    fontWeight: "500",
  },

  helpText: {
    color: "#6B7280",
    fontSize: 11,
    lineHeight: 16,
    paddingHorizontal: 16,
    marginBottom: 10,
  },

  scrollWrap: {
    marginBottom: 14,
  },
  scrollContent: {
    paddingHorizontal: 12,
  },

  gridRow: {
    flexDirection: "row",
    alignItems: "center",
  },

  hourHeaderCell: {
    justifyContent: "flex-end",
    alignItems: "center",
    paddingBottom: 2,
  },
  hourLabelMajor: {
    color: "#9CA3AF",
    fontSize: 9,
    fontWeight: "600",
  },
  hourLabelMinor: {
    color: "#374151",
    fontSize: 8,
  },

  dayLabel: {
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
  },
  dayLabelActive: {
    backgroundColor: "#2D1F1F",
  },
  dayLabelText: {
    color: "#9CA3AF",
    fontSize: 11,
    fontWeight: "600",
  },
  dayLabelTextActive: {
    color: "#F97777",
  },

  cell: {
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
  },
  cellOpen: {
    backgroundColor: "#1A2E2D",
  },
  cellBlocked: {
    backgroundColor: "#D94F4F",
  },
  cellPeakHint: {
    backgroundColor: "#1E3A38",
  },
  peakDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: "#51B868",
    opacity: 0.6,
  },

  legend: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 16,
    paddingBottom: 14,
    paddingHorizontal: 16,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  legendSwatch: {
    width: 14,
    height: 14,
    borderRadius: 3,
    justifyContent: "center",
    alignItems: "center",
  },
  legendLabel: {
    color: "#9CA3AF",
    fontSize: 11,
  },
});
